export const calculateGrowthSnapshotsFromDeposits = (
  startYear,
  ratePercent,
  years,
  deposits,
  startMonth = 9,
  interval = 4,
) => {
  const result = {};
  let balance = 0;

  const monthlyRate = Math.pow(1 + ratePercent / 100, 1 / 12) - 1;
  let monthCounter = 0;
  let started = false;

  for (let y = 0; y < years; y++) {
    const year = startYear + y;
    result[year] = {};

    for (let month = 1; month <= 12; month++) {
      const key = `${year}-${String(month).padStart(2, '0')}`;

      if (deposits[key]) {
        balance += deposits[key];
      }

      if (!started) {
        if (month === startMonth && y === 0) {
          result[year][month] = Number(balance.toFixed(2));
          started = true;
          monthCounter = 0;
        }
      } else {
        balance *= 1 + monthlyRate;
        monthCounter++;

        if (monthCounter % interval === 0) {
          result[year][month] = Number(balance.toFixed(2));
        }
      }
    }
  }

  return result;
};

export const flattenGrowthResult = (
  result,
  data = [],
  accountNames = 'all',
) => {
  const output = [];

  if (typeof accountNames === 'string' && accountNames !== 'all') {
    accountNames = accountNames === 'yi' ? ['yi', 'yiIB'] : [accountNames];
  }

  for (const year in result) {
    const shortYear = year.slice(-2);

    for (const monthName in result[year]) {
      const date = `${monthName}/${shortYear}`;

      const found = data.find(
        (d) => d.year === Number(year) && d.month === Number(monthName),
      );

      const item = {
        date,
        estimate: parseFloat(result[year][monthName]),
      };

      if (found) {
        let real = 0;

        if (accountNames === 'all') {
          real = Object.values(found.accounts).reduce((sum, acc) => {
            return sum + acc.bond + acc.stock + acc.cash;
          }, 0);
        } else {
          real = accountNames.reduce((sum, name) => {
            if (found.accounts[name]) {
              const acc = found.accounts[name];
              return sum + acc.bond + acc.stock + acc.cash;
            }
            return sum;
          }, 0);
        }

        item.real = Math.round(real * 100) / 100;
      }

      output.push(item);
    }
  }

  output.sort((a, b) => {
    const [monthA, yearA] = a.date.split('/').map(Number);
    const [monthB, yearB] = b.date.split('/').map(Number);
    return yearA - yearB || monthA - monthB;
  });

  return output;
};

export const getDeposit = (entries, selectedAccounts = null) => {
  return entries.reduce((acc, entry) => {
    const date = entry.date;
    const accounts = entry.accounts;

    const filteredAccounts = selectedAccounts
      ? Object.keys(accounts)
          .filter((key) => selectedAccounts.includes(key))
          .reduce((obj, key) => {
            obj[key] = accounts[key];
            return obj;
          }, {})
      : accounts;

    const total = Object.values(filteredAccounts).reduce(
      (sum, val) => sum + val,
      0,
    );

    const roundedTotal = Math.round(total * 100) / 100;

    if (acc[date]) {
      acc[date] = Math.round((acc[date] + roundedTotal) * 100) / 100;
    } else {
      acc[date] = roundedTotal;
    }

    return acc;
  }, {});
};

const calculateTotalDeposit = (entries) => {
  const individualTotals = {};
  let total = 0;

  entries.forEach((entry) => {
    for (const [account, value] of Object.entries(entry.accounts)) {
      total += value;
      if (!individualTotals[account]) {
        individualTotals[account] = 0;
      }
      individualTotals[account] += value;
    }
  });

  return {
    total,
    individualTotals,
  };
};

export const buildTotalDeposit = (account, deposit) => {
  const totalDeposit = calculateTotalDeposit(deposit);
  switch (account) {
    case 'all':
      return totalDeposit.total;
    default:
      return totalDeposit.individualTotals[account];
  }
};

export const buildCurrentTotalInvest = (account, deposit) => {
  let total;

  if (account === 'all') {
    total = Object.values(deposit.accounts).reduce(
      (sum, value) => sum + value,
      0,
    );
  } else if (account === 'yi') {
    total = (deposit.accounts.yi || 0) + (deposit.accounts.yiIB || 0);
  } else {
    total = deposit.accounts[account];
  }

  return Number(total.toFixed(2));
};

export const buildDepositByIB = (account, deposits) => {
  const totals = deposits.reduce(
    (acc, entry) => {
      const value =
        account === 'all'
          ? Object.values(entry.accounts).reduce((sum, val) => sum + val, 0)
          : entry.accounts[account] || 0;
      if (entry.isIB) {
        acc.ib += value;
      } else {
        acc.hl += value;
      }
      return acc;
    },
    { hl: 0, ib: 0 },
  );

  return [
    { name: 'HL', value: Number(totals.hl.toFixed(2)) },
    { name: 'IB', value: Number(totals.ib.toFixed(2)) },
  ];
};

export const calculateDifference = (totalInvest, currentTotalInvest) => {
  const diff = currentTotalInvest - totalInvest;
  const percent = totalInvest === 0 ? 0 : (diff / totalInvest) * 100;
  return `${diff > 0 ? '+' : ''}£${diff.toFixed(2)} (${percent > 0 ? '+' : ''}${percent.toFixed(2)}%)`;
};
